import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useIsPresent } from 'framer-motion';
import { useEffect, useRef, useState, type ComponentProps, type FormEvent, type ReactNode } from 'react';
import {
  ArrowRight,
  Check,
  ChevronDown,
  CircleCheck,
  CircleHelp,
  Copy,
  CreditCard,
} from 'lucide-react';
import { PixIcon } from '@/components/checkout-public/branded-icons';
import * as Dialog from '@radix-ui/react-dialog';
import './reservei-checkout.css';

export type PlanId = 'essential' | 'starter' | 'growth';
type Cycle = 'monthly' | 'annual';
type PaymentMethod = 'pix' | 'card';
type CardScenario = 'approved' | 'declined';
type PreviewStage = 'plans' | 'checkout' | 'pix' | 'card' | 'success';
type CheckoutStep = 'data' | 'payment' | 'review';
type MotionDirection = 'forward' | 'back';
type MotionScope = 'view' | 'step' | 'result';
type CatalogOrigin = Exclude<PreviewStage, 'plans'>;
type PixCopyState = 'idle' | 'copied' | 'failed';

type CheckoutResult =
  | { kind: 'pix'; status: 'awaiting' | 'confirmed' | 'expired'; codeVersion: number; expiresAt: number }
  | { kind: 'card'; scenario: CardScenario };

export const PLAN_META: Record<
  PlanId,
  { name: string; monthly: number; annual: number; benefits: string[] }
> = {
  essential: {
    name: 'Essencial',
    monthly: 34.99,
    annual: 349.9,
    benefits: ['Agendamentos ilimitados', '1 profissional', 'Página de agendamento + QR Code pra divulgar'],
  },
  starter: {
    name: 'Starter',
    monthly: 79.9,
    annual: 799,
    benefits: ['Tudo do Essencial +', 'Até 3 profissionais com agendas separadas', 'IA no WhatsApp atende sozinha (100 respostas por dia)'],
  },
  growth: {
    name: 'Growth',
    monthly: 179,
    annual: 1790,
    benefits: ['Tudo do Starter +', 'Até 10 profissionais com agendas separadas', 'IA no WhatsApp 24/7 (500 respostas por dia — 5× o Starter)'],
  },
};

const PLAN_ORDER: PlanId[] = ['essential', 'starter', 'growth'];
const STEP_ORDER: CheckoutStep[] = ['data', 'payment', 'review'];
const COUPON_CODE = 'DEMO10';
const PIX_DEMO_CODE = 'PIX-DEMO-NAO-PAGAVEL';
const PIX_TTL_MS = 5 * 60 * 1000;
const DEMO_QR_SIZE = 29;

function isDemoQrFinderModule(row: number, column: number, top: number, left: number) {
  const localRow = row - top;
  const localColumn = column - left;

  if (localRow < 0 || localRow >= 7 || localColumn < 0 || localColumn >= 7) return false;

  const edge = localRow === 0 || localRow === 6 || localColumn === 0 || localColumn === 6;
  const center = localRow >= 2 && localRow <= 4 && localColumn >= 2 && localColumn <= 4;
  return edge || center;
}

function createDemoQrMatrix() {
  return Array.from({ length: DEMO_QR_SIZE }, (_, row) =>
    Array.from({ length: DEMO_QR_SIZE }, (_, column) => {
      if (
        isDemoQrFinderModule(row, column, 0, 0) ||
        isDemoQrFinderModule(row, column, 0, DEMO_QR_SIZE - 7) ||
        isDemoQrFinderModule(row, column, DEMO_QR_SIZE - 7, 0)
      ) {
        return true;
      }

      const reserved =
        (row < 8 && column < 8) ||
        (row < 8 && column >= DEMO_QR_SIZE - 8) ||
        (row >= DEMO_QR_SIZE - 8 && column < 8);
      if (reserved) return false;

      return ((row * 17 + column * 31 + row * column) % 11) < 5;
    }),
  );
}

const DEMO_QR_MATRIX = createDemoQrMatrix();

function formatPrice(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function roundCents(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function Price({ value, suffix }: { value: number; suffix: string }) {
  const [whole, decimal] = value.toFixed(2).split('.');

  return (
    <span className="reservei-preview__price">
      <span className="reservei-preview__price-currency">R$</span>
      <span>{Number(whole).toLocaleString('pt-BR')}</span>
      <span className="reservei-preview__price-decimal">,{decimal}</span>
      <span className="reservei-preview__price-suffix">{suffix}</span>
    </span>
  );
}

function planPrice(plan: (typeof PLAN_META)[PlanId], cycle: Cycle) {
  return cycle === 'monthly' ? plan.monthly : plan.annual;
}

type PresenceSceneProps = ComponentProps<typeof motion.div> & {
  children: ReactNode;
  sceneId: string;
  sceneRef?: { current: HTMLDivElement | null };
};

function PresenceScene({ children, sceneId, sceneRef: externalRef, ...props }: PresenceSceneProps) {
  const isPresent = useIsPresent();
  const sceneRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (sceneRef.current) sceneRef.current.inert = !isPresent;
  }, [isPresent]);

  const setSceneRef = (node: HTMLDivElement | null) => {
    sceneRef.current = node;
    if (externalRef) externalRef.current = node;
  };

  return (
    <motion.div
      ref={setSceneRef}
      data-motion-scene={sceneId}
      aria-hidden={isPresent ? undefined : true}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export default function ReserveiCheckoutPreview() {
  const reducedMotionPreference = useReducedMotion();
  const prefersReducedMotion = reducedMotionPreference !== false;
  const [planId, setPlanId] = useState<PlanId>('starter');
  const [draftPlanId, setDraftPlanId] = useState<PlanId>('starter');
  const [cycle, setCycle] = useState<Cycle>('monthly');
  const [stage, setStage] = useState<PreviewStage>('checkout');
  const [step, setStep] = useState<CheckoutStep>('data');
  const [catalogOrigin, setCatalogOrigin] = useState<CatalogOrigin>('checkout');
  const [method, setMethod] = useState<PaymentMethod>('pix');
  const [cardScenario, setCardScenario] = useState<CardScenario>('approved');
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [coupon, setCoupon] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponMessage, setCouponMessage] = useState('');
  const [name, setName] = useState('Marina Oliveira');
  const [email, setEmail] = useState('marina@example.com');
  const [result, setResult] = useState<CheckoutResult | null>(null);
  const [pixCodeVersion, setPixCodeVersion] = useState(1);
  const [pixSecondsLeft, setPixSecondsLeft] = useState(0);
  const [pixCopyState, setPixCopyState] = useState<PixCopyState>('idle');
  const [dockOpen, setDockOpen] = useState(false);
  const [motionDirection, setMotionDirection] = useState<MotionDirection | null>(null);
  const [motionToken, setMotionToken] = useState(0);
  const submissionGuardRef = useRef(false);
  const transitionLockRef = useRef(false);
  const motionScopeRef = useRef<MotionScope | null>(null);
  const [transitionActive, setTransitionActive] = useState(false);
  const planHeadingRef = useRef<HTMLHeadingElement>(null);
  const checkoutHeadingRef = useRef<HTMLHeadingElement>(null);
  const resultRef = useRef<HTMLDivElement | null>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const previewRootRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const copyRequestRef = useRef(0);

  const startMotion = (direction: MotionDirection, scope: MotionScope) => {
    if (transitionLockRef.current) return false;
    transitionLockRef.current = !prefersReducedMotion;
    motionScopeRef.current = scope;
    setTransitionActive(!prefersReducedMotion);
    setMotionDirection(direction);
    setMotionToken((token) => token + 1);
    return true;
  };

  const completeMotion = (scope: MotionScope) => {
    if (motionScopeRef.current !== scope) return;
    transitionLockRef.current = false;
    motionScopeRef.current = null;
    setTransitionActive(false);
  };

  const focusIncoming = (heading: HTMLElement | null, scene?: HTMLElement | null) => {
    if (!heading) return;
    const active = document.activeElement;
    const incomingScene = scene ?? heading.closest<HTMLElement>('[data-motion-scene]');
    if (
      active instanceof HTMLElement &&
      incomingScene?.contains(active) &&
      /^(INPUT|BUTTON|SELECT|TEXTAREA|A|SUMMARY)$/.test(active.tagName)
    ) {
      return;
    }
    heading.focus();
  };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Prévia Reservei | Checkout';
    const existing = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const robots = existing ?? document.createElement('meta');
    const previousContent = robots.getAttribute('content');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    if (!existing) document.head.appendChild(robots);

    return () => {
      document.title = previousTitle;
      if (existing) {
        if (previousContent === null) existing.removeAttribute('content');
        else existing.content = previousContent;
      } else {
        robots.remove();
      }
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      transitionLockRef.current = false;
      motionScopeRef.current = null;
      copyRequestRef.current += 1;
    };
  }, []);

  useEffect(() => {
    const dock = dockRef.current;
    const root = document.querySelector<HTMLElement>('.reservei-preview');
    if (!dock || !root || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => root.style.setProperty('--reservei-dock-height', `${dock.offsetHeight}px`));
    observer.observe(dock);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--reservei-dock-height');
    };
  }, [stage, step, result, dockOpen]);

  const plan = PLAN_META[planId];
  const total = planPrice(plan, cycle);
  const firstCharge = couponApplied ? roundCents(total * 0.9) : total;
  const nameError = name.trim().length === 0 ? 'Informe seu nome.' : '';
  const emailError = !/^\S+@\S+\.\S+$/.test(email.trim()) ? 'Informe um e-mail válido.' : '';
  const canContinue = !nameError && !emailError;
  const viewVariants = {
    initial: (direction: MotionDirection) => prefersReducedMotion
      ? { opacity: 1, x: 0 }
      : { opacity: 0, x: direction === 'forward' ? 48 : -48 },
    animate: { opacity: 1, x: 0 },
    exit: (direction: MotionDirection) => prefersReducedMotion
      ? { opacity: 1, x: 0 }
      : { opacity: 0, x: direction === 'forward' ? -38 : 38 },
  };

  const pixExpiresAt = result?.kind === 'pix' && result.status === 'awaiting' ? result.expiresAt : null;
  const pixDisplayCode = result?.kind === 'pix' ? `${PIX_DEMO_CODE} · código local ${result.codeVersion}` : PIX_DEMO_CODE;

  useEffect(() => {
    if (stage !== 'pix' || pixExpiresAt === null) return undefined;

    const updatePixClock = () => {
      const remaining = Math.max(0, pixExpiresAt - Date.now());
      setPixSecondsLeft(Math.ceil(remaining / 1000));
      if (remaining === 0) {
        submissionGuardRef.current = false;
        copyRequestRef.current += 1;
        setPixCopyState('idle');
        setResult((current) => (
          current?.kind === 'pix' && current.status === 'awaiting' && current.expiresAt === pixExpiresAt
            ? { ...current, status: 'expired' }
            : current
        ));
      }
    };

    updatePixClock();
    const intervalId = window.setInterval(updatePixClock, 1000);
    return () => window.clearInterval(intervalId);
  }, [pixExpiresAt, stage]);

  const invalidateResult = () => {
    if (stage !== 'plans' && stage !== 'checkout' && !startMotion('back', 'step')) return;
    submissionGuardRef.current = false;
    copyRequestRef.current += 1;
    setPixCopyState('idle');
    setPixSecondsLeft(0);
    setResult(null);
    if (stage !== 'plans' && stage !== 'checkout') {
      setStage('checkout');
      setStep('payment');
    }
  };

  const couponAppliedMessage = (amount: number) =>
    `${COUPON_CODE} aplicado: 10% na primeira cobrança (${formatPrice(roundCents(amount * 0.9))}).`;

  const simulateReview = () => {
    if (!canContinue) {
      submissionGuardRef.current = false;
      setResult(null);
      document.getElementById(nameError ? 'preview-name' : 'preview-email')?.focus();
      return;
    }
    if (submissionGuardRef.current) return;
    if (!startMotion('forward', 'result')) return;
    submissionGuardRef.current = true;

    if (method === 'pix') {
      copyRequestRef.current += 1;
      setPixCopyState('idle');
      setPixSecondsLeft(Math.ceil(PIX_TTL_MS / 1000));
      setResult({ kind: 'pix', status: 'awaiting', codeVersion: pixCodeVersion, expiresAt: Date.now() + PIX_TTL_MS });
      setStage('pix');
      return;
    }

    setResult({ kind: 'card', scenario: cardScenario });
    setStage(cardScenario === 'approved' ? 'success' : 'card');
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (step === 'data') {
      if (!canContinue) {
        document.getElementById(nameError ? 'preview-name' : 'preview-email')?.focus();
        return;
      }
      moveToStep('payment');
      return;
    }
    if (step === 'payment') {
      moveToStep('review');
      return;
    }
    if (step === 'review') simulateReview();
  };

  const applyCoupon = () => {
    invalidateResult();
    const normalized = coupon.trim().toUpperCase();
    const nextCouponApplied = normalized === COUPON_CODE;
    if (!normalized) {
      setCouponApplied(false);
      setCouponMessage('Digite um cupom para continuar.');
      return;
    }
    if (normalized === COUPON_CODE) {
      setCoupon(normalized);
      setCouponApplied(true);
      setCouponMessage(couponAppliedMessage(total));
      return;
    }
    setCouponApplied(false);
    setCouponMessage('Cupom demonstrativo não reconhecido; nenhum desconto foi aplicado.');
  };

  const removeCoupon = () => {
    invalidateResult();
    setCoupon('');
    setCouponApplied(false);
    setCouponMessage('Cupom removido.');
  };

  const restartPreview = () => {
    if (!startMotion('back', 'step')) return;
    submissionGuardRef.current = false;
    setStage('checkout');
    setStep('data');
    setPlanId('starter');
    setDraftPlanId('starter');
    setCycle('monthly');
    setMethod('pix');
    setCardScenario('approved');
    setSummaryOpen(false);
    setCouponOpen(false);
    setCoupon('');
    setCouponApplied(false);
    setCouponMessage('');
    setName('Marina Oliveira');
    setEmail('marina@example.com');
    setResult(null);
    setPixSecondsLeft(0);
    setPixCopyState('idle');
    copyRequestRef.current += 1;
    setPixCodeVersion(1);
  };

  const choosePlan = (nextPlan: PlanId) => {
    if (nextPlan === draftPlanId) return;
    setDraftPlanId(nextPlan);
  };

  const openPlanCatalog = () => {
    if (transitionLockRef.current || !startMotion('back', 'view')) return;
    setCatalogOrigin(stage === 'plans' ? 'checkout' : stage);
    setDraftPlanId(planId);
    setSummaryOpen(false);
    setStage('plans');
  };

  const cancelPlanCatalog = () => {
    if (!startMotion('back', 'view')) return;
    setStage(catalogOrigin);
  };

  const confirmPlanSelection = () => {
    if (transitionLockRef.current) return;
    const nextPlan = draftPlanId;
    const origin = catalogOrigin;
    if (!startMotion('back', 'view')) return;
    if (nextPlan === planId) {
      setStage(origin);
      return;
    }
    submissionGuardRef.current = false;
    setResult(null);
    setPixSecondsLeft(0);
    setPixCopyState('idle');
    copyRequestRef.current += 1;
    setPixCodeVersion((version) => version + 1);
    setPlanId(nextPlan);
    if (couponApplied) setCouponMessage(couponAppliedMessage(planPrice(PLAN_META[nextPlan], cycle)));
    setStage('checkout');
    setStep('data');
  };

  const chooseCycle = (nextCycle: Cycle) => {
    if (transitionLockRef.current || nextCycle === cycle) return;
    invalidateResult();
    setCycle(nextCycle);
    if (couponApplied) setCouponMessage(couponAppliedMessage(planPrice(plan, nextCycle)));
  };

  const chooseMethod = (nextMethod: PaymentMethod) => {
    if (transitionLockRef.current) return;
    invalidateResult();
    setMethod(nextMethod);
  };

  const advancePix = (status: 'confirmed' | 'expired') => {
    if (status === 'confirmed' && result?.kind === 'pix' && result.status === 'awaiting' && result.expiresAt <= Date.now()) {
      submissionGuardRef.current = false;
      copyRequestRef.current += 1;
      setPixCopyState('idle');
      setPixSecondsLeft(0);
      setResult({ ...result, status: 'expired' });
      return;
    }
    if (!startMotion(status === 'confirmed' ? 'forward' : 'back', 'result')) return;
    copyRequestRef.current += 1;
    setPixCopyState('idle');
    if (status === 'expired') setPixSecondsLeft(0);
    setResult((current) => (current?.kind === 'pix' ? { ...current, status } : current));
    if (status === 'confirmed') {
      setStage('success');
    }
  };

  const regeneratePix = () => {
    if (!startMotion('forward', 'result')) return;
    copyRequestRef.current += 1;
    setPixCopyState('idle');
    setPixSecondsLeft(Math.ceil(PIX_TTL_MS / 1000));
    setPixCodeVersion((version) => version + 1);
    setResult({ kind: 'pix', status: 'awaiting', codeVersion: pixCodeVersion + 1, expiresAt: Date.now() + PIX_TTL_MS });
  };

  const copyPixCode = async () => {
    const requestId = copyRequestRef.current + 1;
    copyRequestRef.current = requestId;
    const codeToCopy = pixDisplayCode;
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (!clipboard?.writeText) {
      if (mountedRef.current && copyRequestRef.current === requestId) setPixCopyState('failed');
      return;
    }
    try {
      await clipboard.writeText(codeToCopy);
      if (mountedRef.current && copyRequestRef.current === requestId) setPixCopyState('copied');
    } catch {
      if (mountedRef.current && copyRequestRef.current === requestId) setPixCopyState('failed');
    }
  };

  const retryCard = () => {
    if (!startMotion('back', 'step')) return;
    submissionGuardRef.current = false;
    setResult(null);
    setStage('checkout');
    setStep('payment');
  };

  const stepLabels: Record<CheckoutStep, string> = { data: 'Dados', payment: 'Pagamento', review: 'Revisão' };
  const currentStepIndex = STEP_ORDER.indexOf(step);

  const moveToStep = (target: CheckoutStep) => {
    const targetIndex = STEP_ORDER.indexOf(target);
    if (!startMotion(targetIndex >= currentStepIndex ? 'forward' : 'back', 'step')) return false;
    setStep(target);
    return true;
  };

  const handleDockPrimary = (event?: { detail: number }) => {
    if (event && event.detail > 1) return;
    if (transitionLockRef.current) return;
    if (stage === 'plans') {
      confirmPlanSelection();
      return;
    }
    if (stage === 'pix' && result?.kind === 'pix') {
      if (result.status === 'awaiting') advancePix('confirmed');
      else regeneratePix();
      return;
    }
    if (stage === 'card') {
      retryCard();
      return;
    }
    if (stage === 'success') {
      restartPreview();
      return;
    }
    if (step === 'data') {
      if (!canContinue) {
        document.getElementById(nameError ? 'preview-name' : 'preview-email')?.focus();
        return;
      }
      moveToStep('payment');
      return;
    }
    if (step === 'payment') {
      moveToStep('review');
      return;
    }
    simulateReview();
  };

  const handleDockDialogPrimary = (event?: { detail: number }) => {
    if (event && event.detail > 1) return;
    if (transitionLockRef.current) return;
    const invalidData = stage === 'checkout' && step === 'data' && !canContinue;
    if (invalidData) pendingFocusRef.current = nameError ? 'preview-name' : 'preview-email';
    handleDockPrimary();
    setDockOpen(false);
  };

  const goToStep = (target: CheckoutStep) => {
    if (stage === 'checkout' && target === step) return;
    if (!moveToStep(target)) return;
    submissionGuardRef.current = false;
    copyRequestRef.current += 1;
    setPixCopyState('idle');
    setPixSecondsLeft(0);
    setResult(null);
    setStage('checkout');
  };

  const dockCta = stage === 'plans'
    ? `Usar plano ${PLAN_META[draftPlanId].name}`
    : stage === 'pix'
      ? result?.kind === 'pix' && result.status === 'expired' ? 'Gerar novo Pix' : 'Confirmar demo'
      : stage === 'card'
        ? 'Tentar novamente'
        : stage === 'success'
          ? 'Reiniciar prévia'
          : step === 'data'
            ? 'Continuar para pagamento'
            : step === 'payment'
              ? 'Revisar pedido'
              : `Simular ${method === 'pix' ? 'Pix' : 'cartão'}`;
  const visibleStepIndex = stage === 'checkout' ? currentStepIndex : STEP_ORDER.length - 1;
  const stepProgress = visibleStepIndex / (STEP_ORDER.length - 1);

  return (
    <div className="reservei-preview" ref={previewRootRef}>
      <header className="reservei-preview__header">
        <a className="reservei-preview__brand" href="#preview-note" aria-label="Reservei">
          <img src="/preview/reservei-checkout/logo-tight.png" alt="" />
          <span>eservei</span>
        </a>
        <a className="reservei-preview__help reservei-preview__help--desktop" href={stage === 'plans' ? '#preview-plan-help' : '#preview-support-desktop'}>
          <CircleHelp size={19} aria-hidden="true" />
          Ajuda
        </a>
        <a className="reservei-preview__help reservei-preview__help--mobile" href={stage === 'plans' ? '#preview-plan-help' : '#preview-support-mobile'}>
          Ajuda
        </a>
      </header>

      <main className="reservei-preview__main">
        {stage !== 'plans' && <nav className="reservei-preview__stepper" aria-label="Etapas da prévia">
          {STEP_ORDER.map((stepName, index) => {
            const isCurrent = index === visibleStepIndex;
            const isPast = index < visibleStepIndex;
            return (
              <button
                type="button"
                key={stepName}
                aria-current={isCurrent ? 'step' : undefined}
                disabled={index > visibleStepIndex}
                className={isCurrent ? 'is-current' : isPast ? 'is-past' : ''}
                onClick={() => { if (index <= visibleStepIndex) goToStep(stepName); }}
              >
                <span>{index + 1}</span>{stepLabels[stepName]}
              </button>
            );
          })}
          <span className="reservei-preview__stepper-track" aria-hidden="true">
            <motion.span
              className="reservei-preview__stepper-progress"
              initial={false}
              animate={{ width: `${stepProgress * 100}%` }}
              transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            />
          </span>
        </nav>}
        <AnimatePresence initial={false} mode="wait" custom={motionDirection ?? 'forward'}>
        {stage === 'plans' ? (
          <PresenceScene
            key="plans-view"
            sceneId="plans-view"
            className="reservei-preview__plan-selection"
            data-motion-direction={motionDirection ?? undefined}
            data-motion-token={motionDirection ? motionToken % 2 : undefined}
            data-motion-scene="plans"
            custom={motionDirection ?? 'forward'}
            variants={viewVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
            onAnimationComplete={(definition) => {
              if (definition !== 'animate') return;
              completeMotion('view');
              focusIncoming(planHeadingRef.current);
            }}
            aria-labelledby="plan-selection-title"
          >
            <p className="reservei-preview__preview-note" id="preview-note">Prévia visual · sem cobrança</p>
            <h1 id="plan-selection-title" ref={planHeadingRef} tabIndex={-1}>Escolha seu plano</h1>
            <p className="reservei-preview__plan-selection-copy">Selecione uma opção e confirme para voltar à prévia de checkout.</p>
            <button type="button" className="reservei-preview__plan-cancel" onClick={cancelPlanCatalog}>Cancelar e voltar ao checkout</button>
            <div className="reservei-preview__plan-cards" role="group" aria-label="Planos disponíveis">
              {PLAN_ORDER.map((option) => {
                const optionPlan = PLAN_META[option];
                return (
                  <button type="button" key={option} className={`reservei-preview__plan-card${option === draftPlanId ? ' is-selected' : ''}`} aria-pressed={option === draftPlanId} onClick={() => choosePlan(option)}>
                    {option === draftPlanId && <motion.span className="reservei-preview__selection-highlight" initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.22 }} aria-hidden="true" />}
                    <span className="reservei-preview__plan-card-name">{optionPlan.name}</span>
                    <Price value={optionPlan.monthly} suffix="/mês" />
                    <span className="reservei-preview__plan-card-annual">{formatPrice(optionPlan.annual)}/ano</span>
                    <span className="reservei-preview__plan-card-benefit">{optionPlan.benefits[1]}</span>
                  </button>
                );
              })}
            </div>
            <p className="reservei-preview__plan-help" id="preview-plan-help">Escolha local, sem cadastro, cobrança ou envio de dados.</p>
          </PresenceScene>
        ) : (
          <PresenceScene
            key="checkout-view"
            sceneId="checkout-view"
            className="reservei-preview__checkout-view"
            data-motion-scene="checkout"
            custom={motionDirection ?? 'forward'}
            variants={viewVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
            onAnimationComplete={(definition) => {
              if (definition !== 'animate') return;
              completeMotion('view');
              focusIncoming(checkoutHeadingRef.current);
            }}
          >
        <div className="reservei-preview__utility-bar">
          <p className="reservei-preview__preview-note" id="preview-note">
            Prévia visual · sem cobrança
          </p>
        </div>

        <div
          className="reservei-preview__layout"
          data-motion-direction={motionDirection ?? undefined}
          data-motion-token={motionDirection ? motionToken % 2 : undefined}
        >
          <section className="reservei-preview__intro" aria-labelledby="preview-title">
            <h1 id="preview-title" ref={checkoutHeadingRef} tabIndex={-1} aria-label="Assine o Reservei">
              <span>Assine o</span>
              <span>Reservei</span>
            </h1>
            <p>Confira seus dados para continuar.</p>
          </section>

          <aside className="reservei-preview__summary" aria-label="Resumo da assinatura">
            <div className={`reservei-preview__summary-panel${summaryOpen ? ' is-open' : ''}`}>
              <p className="reservei-preview__eyebrow">Resumo</p>
              <div className="reservei-preview__summary-heading">
                <span className="reservei-preview__calendar" aria-hidden="true">
                  <img src="/preview/reservei-checkout/calendario.png" alt="" />
                </span>
                <div className="reservei-preview__summary-details">
                  <h2>Plano {plan.name}</h2>
                  <span className="reservei-preview__summary-cycle-label">
                    {cycle === 'monthly' ? 'Mensal' : 'Anual'}
                  </span>
                </div>
                <div className="reservei-preview__summary-price">
                  <Price value={total} suffix={cycle === 'monthly' ? '/mês' : '/ano'} />
                  {cycle === 'annual' && (
                    <p className="reservei-preview__annual-note">
                      {formatPrice(plan.annual / 12)}/mês equivalente
                    </p>
                  )}
                </div>
              </div>

              <button type="button" className="reservei-preview__plan-change" onClick={openPlanCatalog}>
                Trocar plano
              </button>

              <div className="reservei-preview__summary-expanded">
              <div className="reservei-preview__summary-expanded-content">
              <div className="reservei-preview__cycle-switch" role="group" aria-label="Escolha a cadência">
                <button type="button" className={cycle === 'monthly' ? 'is-selected' : ''} aria-pressed={cycle === 'monthly'} onClick={() => chooseCycle('monthly')}>
                  {cycle === 'monthly' && <motion.span className="reservei-preview__selection-highlight" initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2 }} aria-hidden="true" />}
                  Mensal · {formatPrice(plan.monthly)}
                </button>
                <button type="button" className={cycle === 'annual' ? 'is-selected' : ''} aria-pressed={cycle === 'annual'} onClick={() => chooseCycle('annual')}>
                  {cycle === 'annual' && <motion.span className="reservei-preview__selection-highlight" initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2 }} aria-hidden="true" />}
                  Anual · {formatPrice(plan.annual)}
                </button>
              </div>

              <div className="reservei-preview__benefits" aria-label="Benefícios do plano">
                {plan.benefits.map((benefit) => (
                  <div key={benefit}>
                    <Check size={18} aria-hidden="true" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
              </div>
              </div>

              <button
                type="button"
                className="reservei-preview__summary-toggle"
                aria-expanded={summaryOpen}
                onClick={() => setSummaryOpen((open) => !open)}
              >
                <span>{summaryOpen ? 'Ocultar ciclo e benefícios' : 'Ver ciclo e benefícios'}</span>
                <ChevronDown className={summaryOpen ? 'is-open' : ''} size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="reservei-preview__support" id="preview-support-desktop">
              <img src="/preview/reservei-checkout/aceno.png" alt="" />
              <p>
                <span>{stage === 'success' ? 'Resumo da simulação acima.' : 'Confira tudo com calma.'}</span>
                <span>{stage === 'success' ? 'Ajuda local, sem rede.' : 'Esta tela é uma prévia local.'}</span>
              </p>
            </div>
          </aside>

          <section
            className="reservei-preview__form-column"
            data-motion-direction={motionDirection ?? undefined}
            data-motion-token={motionDirection ? motionToken % 2 : undefined}
            aria-label="Dados e pagamento"
          >
            <AnimatePresence initial={false} mode="wait" custom={motionDirection ?? 'forward'}>
            {stage === 'checkout' && (
              <PresenceScene
                key={`checkout-${step}`}
                sceneId={`checkout-${step}`}
                className="reservei-preview__animated-scene"
                custom={motionDirection ?? 'forward'}
                variants={viewVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
                onAnimationComplete={(definition) => {
                  if (definition !== 'animate' || motionScopeRef.current !== 'step') return;
                  completeMotion('step');
                  const titleId = step === 'data' ? 'preview-data-title' : step === 'payment' ? 'preview-payment-title' : 'preview-review-title';
                  focusIncoming(document.getElementById(titleId));
                }}
              >
              <form className="reservei-preview__form" onSubmit={handleSubmit} noValidate>
              {step === 'data' && (
              <div className="reservei-preview__animated-step">
                <h2 id="preview-data-title" tabIndex={-1}>Seus dados</h2>
                <div className="reservei-preview__field">
                  <label htmlFor="preview-name">Nome completo</label>
                  <input id="preview-name" value={name} aria-invalid={Boolean(nameError)} onChange={(event) => { invalidateResult(); setName(event.target.value); }} />
                  {nameError && <span className="reservei-preview__error">{nameError}</span>}
                </div>
                <div className="reservei-preview__field">
                  <label htmlFor="preview-email">E-mail</label>
                  <input id="preview-email" type="email" value={email} aria-invalid={Boolean(emailError)} onChange={(event) => { invalidateResult(); setEmail(event.target.value); }} />
                  {emailError && <span className="reservei-preview__error">{emailError}</span>}
                </div>
              </div>
              )}

              {step === 'payment' && (
              <div className="reservei-preview__animated-step">
                <fieldset className="reservei-preview__payment">
                  <legend id="preview-payment-title" tabIndex={-1}>Pagamento</legend>
                  <div className="reservei-preview__payment-options">
                    <label className={method === 'pix' ? 'is-selected' : ''}>
                      {method === 'pix' && <motion.span className="reservei-preview__selection-highlight" initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2 }} aria-hidden="true" />}
                      <input type="radio" name="preview-method" value="pix" checked={method === 'pix'} onChange={() => chooseMethod('pix')} />
                      <span className="reservei-preview__radio" aria-hidden="true" />
                      <PixIcon size={22} aria-label="" />
                      <span>Pix</span>
                    </label>
                    <label className={method === 'card' ? 'is-selected' : ''}>
                      {method === 'card' && <motion.span className="reservei-preview__selection-highlight" initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2 }} aria-hidden="true" />}
                      <input aria-label="Cartão de crédito" type="radio" name="preview-method" value="card" checked={method === 'card'} onChange={() => chooseMethod('card')} />
                      <span className="reservei-preview__radio" aria-hidden="true" />
                      <CreditCard size={22} aria-hidden="true" />
                      <span>
                        <span className="reservei-preview__method-label-full">Cartão de crédito</span>
                        <span className="reservei-preview__method-label-short">Cartão</span>
                      </span>
                    </label>
                  </div>
                  <p className="reservei-preview__payment-helper">
                    {method === 'pix' ? 'Código demonstrativo; não é escaneável nem pagável.' : 'Cartão visual somente: nenhum PAN ou CVV é coletado.'}
                  </p>
                  {method === 'card' && (
                    <div className="reservei-preview__card-demo" aria-label="Cartão demonstrativo somente leitura">
                      <div>
                        <strong>Cartão demonstrativo</strong>
                        <span>•••• 4242 · somente leitura</span>
                      </div>
                      <div className="reservei-preview__card-fields">
                        <label>Número<input aria-label="Número do cartão (somente leitura)" readOnly value="•••• •••• •••• 4242" /></label>
                        <label>Titular<input aria-label="Titular do cartão (somente leitura)" readOnly value="MARINA OLIVEIRA" /></label>
                        <label>Validade<input aria-label="Validade do cartão (somente leitura)" readOnly value="12/30" /></label>
                        <label>Código<input aria-label="Código fictício (somente leitura)" readOnly value="•••" /></label>
                      </div>
                      <div className="reservei-preview__card-scenarios" role="group" aria-label="Cenário do cartão">
                        {(['approved', 'declined'] as CardScenario[]).map((scenario) => (
                          <button type="button" key={scenario} className={cardScenario === scenario ? 'is-selected' : ''} aria-pressed={cardScenario === scenario} onClick={() => { if (transitionLockRef.current) return; invalidateResult(); setCardScenario(scenario); }}>
                            {scenario === 'approved' ? 'Aprovado' : 'Recusado'}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </fieldset>

                <div className="reservei-preview__coupon-slot">
                  <button type="button" className="reservei-preview__coupon-trigger" aria-expanded={couponOpen} onClick={() => setCouponOpen((open) => !open)}>
                    <span>{couponOpen ? 'Ocultar cupom' : 'Adicionar cupom'}</span>
                    <ChevronDown className={couponOpen ? 'is-open' : ''} size={16} aria-hidden="true" />
                  </button>
                  <AnimatePresence initial={false}>
                  {couponOpen && (
                    <motion.div
                      key="coupon-editor"
                      className="reservei-preview__coupon-form"
                      aria-label="Cupom demonstrativo"
                      initial={prefersReducedMotion ? { opacity: 1, height: 'auto' } : { opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={prefersReducedMotion ? { opacity: 1, height: 'auto' } : { opacity: 0, height: 0 }}
                      transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <label htmlFor="preview-coupon">Cupom</label>
                      <div>
                        <input
                          id="preview-coupon"
                          value={coupon}
                          placeholder="Ex.: DEMO10"
                          onChange={(event) => { invalidateResult(); setCoupon(event.target.value); }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              event.stopPropagation();
                              applyCoupon();
                            }
                          }}
                        />
                        <button type="button" onClick={applyCoupon}>Aplicar</button>
                      </div>
                      <p className="reservei-preview__helper">DEMO10: 10% somente na primeira cobrança; renovação integral.</p>
                      {couponMessage && <p className="reservei-preview__coupon-message" role="status">{couponMessage}</p>}
                      {couponApplied && <button type="button" className="reservei-preview__coupon-remove" onClick={removeCoupon}>Remover cupom</button>}
                    </motion.div>
                  )}
                  </AnimatePresence>
                </div>
              </div>
              )}

              {step === 'review' && (
              <div className="reservei-preview__review-panel reservei-preview__animated-step">
                <h2 id="preview-review-title" tabIndex={-1}>Revisão</h2>
                <p><strong>{plan.name}</strong> · {cycle === 'monthly' ? 'Mensal' : 'Anual'} · {method === 'pix' ? 'Pix demonstrativo' : 'Cartão demonstrativo'}</p>
                <p>{couponApplied ? `Cupom ${COUPON_CODE}: 10% na primeira cobrança.` : 'Sem cupom aplicado.'}</p>
                <p><strong>Primeira cobrança: {formatPrice(firstCharge)}</strong></p>
                <p>{cycle === 'annual' ? `Renovação integral: ${formatPrice(total)} por ano.` : `Renovação mensal: ${formatPrice(total)}.`}</p>
              </div>
              )}
              </form>
              </PresenceScene>
            )}

            {stage === 'pix' && result?.kind === 'pix' && (
              <PresenceScene
                key={`pix-${result.status}`}
                sceneId="pix-result"
                sceneRef={resultRef}
                tabIndex={-1}
                className="reservei-preview__demo-stage reservei-preview__demo-result reservei-preview__demo-result--pix"
                aria-labelledby="pix-stage-title"
                custom={motionDirection ?? 'forward'}
                variants={viewVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                onAnimationComplete={(definition) => {
                  if (definition !== 'animate') return;
                  completeMotion('result');
                  focusIncoming(resultRef.current, resultRef.current);
                }}
              >
                <div className="reservei-preview__pix-shell">
                  <div className="reservei-preview__pix-status-row">
                    <span className="reservei-preview__pix-status">
                      <span className="reservei-preview__pix-status-dot" aria-hidden="true" />
                      {result.status === 'awaiting' ? 'Aguardando pagamento' : 'Prazo encerrado'}
                    </span>
                    <span className="reservei-preview__pix-timer" role="timer" aria-live="off">
                      {Math.floor(pixSecondsLeft / 60).toString().padStart(2, '0')}:{(pixSecondsLeft % 60).toString().padStart(2, '0')}
                    </span>
                  </div>

                  <div className="reservei-preview__pix-title">
                    <PixIcon size={24} aria-hidden="true" />
                    <h2 id="pix-stage-title">{result.status === 'awaiting' ? 'Pague com Pix' : 'Pix expirado'}</h2>
                    <p>{result.status === 'awaiting' ? 'Veja como o Pix apareceria nesta demonstração local.' : 'O prazo desta demonstração terminou. Gere um novo código local para continuar.'}</p>
                  </div>

                  {result.status === 'awaiting' ? (
                    <>
                      <div className="reservei-preview__demo-qr" role="img" aria-label="QR demonstrativo, não escaneável">
                        <svg
                          className="reservei-preview__demo-qr-svg"
                          viewBox={`0 0 ${DEMO_QR_SIZE} ${DEMO_QR_SIZE}`}
                          aria-hidden="true"
                          focusable="false"
                          shapeRendering="crispEdges"
                        >
                          {DEMO_QR_MATRIX.flatMap((row, rowIndex) =>
                            row.map((active, columnIndex) =>
                              active ? (
                                <rect key={`${rowIndex}-${columnIndex}`} x={columnIndex} y={rowIndex} width="1" height="1" />
                              ) : null,
                            ),
                          )}
                        </svg>
                        <span className="reservei-preview__demo-qr-caption">DEMO · NÃO PAGÁVEL</span>
                      </div>

                      <div className="reservei-preview__pix-amount">
                        <span>Valor a pagar</span>
                        <strong>{formatPrice(firstCharge)}</strong>
                      </div>

                      <div className="reservei-preview__pix-code-field">
                        <label htmlFor="preview-pix-code">Código Pix copia e cola</label>
                        <textarea id="preview-pix-code" readOnly value={pixDisplayCode} rows={3} aria-label="Código Pix demo não pagável" />
                        <button type="button" className="reservei-preview__pix-copy" onClick={() => void copyPixCode()}>
                          <Copy size={17} aria-hidden="true" />
                          {pixCopyState === 'copied' ? 'Código copiado' : 'Copiar código Pix'}
                        </button>
                        {pixCopyState === 'failed' && <span role="status">Não foi possível copiar; selecione o código acima.</span>}
                      </div>

                      <div className="reservei-preview__pix-summary" aria-label="Resumo do Pix demo">
                        <span>{plan.name} · {cycle === 'monthly' ? 'Mensal' : 'Anual'} · Pix demonstrativo</span>
                        <strong>Primeira cobrança: {formatPrice(firstCharge)}</strong>
                      </div>

                      <p className="reservei-preview__pix-instructions">
                        Este código é apenas demonstrativo e não pode ser escaneado ou pago. Use Confirmar demo no dock para avançar localmente.
                      </p>

                      <button
                        type="button"
                        className="reservei-preview__pix-expire"
                        onClick={(event) => {
                          if (event.detail > 1) return;
                          advancePix('expired');
                        }}
                      >
                        Expirar Pix demonstrativo
                      </button>
                    </>
                  ) : (
                    <p className="reservei-preview__pix-expired-copy">O prazo terminou. Gere um novo código demo para continuar.</p>
                  )}
                </div>
              </PresenceScene>
            )}

            {stage === 'card' && result?.kind === 'card' && (
              <PresenceScene
                key="card-result"
                sceneId="card-result"
                sceneRef={resultRef}
                tabIndex={-1}
                className="reservei-preview__demo-stage reservei-preview__demo-result reservei-preview__demo-result--card"
                aria-labelledby="card-stage-title"
                custom={motionDirection ?? 'forward'}
                variants={viewVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                onAnimationComplete={(definition) => {
                  if (definition !== 'animate') return;
                  completeMotion('result');
                  focusIncoming(resultRef.current, resultRef.current);
                }}
              >
                <CircleCheck size={22} aria-hidden="true" />
                <div>
                  <strong id="card-stage-title">Cartão demo recusado</strong>
                  <span>Escolha outro cenário e tente novamente. Nenhuma cobrança foi feita.</span>
                </div>
              </PresenceScene>
            )}

            {stage === 'success' && result && (
              <PresenceScene
                key="success-result"
                sceneId="success-result"
                sceneRef={resultRef}
                tabIndex={-1}
                className="reservei-preview__demo-stage reservei-preview__success-stage"
                aria-labelledby="success-stage-title"
                custom={motionDirection ?? 'forward'}
                variants={viewVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                onAnimationComplete={(definition) => {
                  if (definition !== 'animate') return;
                  completeMotion('result');
                  focusIncoming(resultRef.current, resultRef.current);
                }}
              >
                <span className="reservei-preview__success-mark" aria-hidden="true">
                  <svg className="reservei-preview__success-check" viewBox="0 0 64 64" focusable="false">
                    <circle className="reservei-preview__success-ring" cx="32" cy="32" r="27" />
                    <path className="reservei-preview__success-check-path" d="M19 33.5 28 42l17-20" />
                  </svg>
                  <span className="reservei-preview__success-ray reservei-preview__success-ray--one" />
                  <span className="reservei-preview__success-ray reservei-preview__success-ray--two" />
                  <span className="reservei-preview__success-ray reservei-preview__success-ray--three" />
                  <span className="reservei-preview__success-ray reservei-preview__success-ray--four" />
                  <span className="reservei-preview__success-ray reservei-preview__success-ray--five" />
                  <span className="reservei-preview__success-ray reservei-preview__success-ray--six" />
                  <span className="reservei-preview__success-ray reservei-preview__success-ray--seven" />
                  <span className="reservei-preview__success-ray reservei-preview__success-ray--eight" />
                </span>
                <div className="reservei-preview__success-copy">
                  <strong id="success-stage-title">Tudo certo!</strong>
                  <p className="reservei-preview__success-qualification">Simulação concluída — nenhuma cobrança foi feita.</p>
                  <div className="reservei-preview__success-summary" aria-label="Resumo da simulação">
                    <span><b>Plano</b>{plan.name}</span>
                    <span><b>Ciclo</b>{cycle === 'monthly' ? 'Mensal' : 'Anual'}</span>
                    <span><b>Primeira cobrança</b>{formatPrice(firstCharge)}</span>
                    <span><b>Método</b>{method === 'pix' ? 'Pix demonstrativo' : 'Cartão demonstrativo'}</span>
                  </div>
                </div>
              </PresenceScene>
            )}
            </AnimatePresence>

            <div className="reservei-preview__support reservei-preview__support--mobile" id="preview-support-mobile">
              <img src="/preview/reservei-checkout/aceno.png" alt="" />
              <p>
                <span>{stage === 'success' ? 'Resumo da simulação acima.' : 'Confira o plano e os dados antes de continuar.'}</span>
                <span>{stage === 'success' ? 'Ajuda local, sem rede.' : 'Esta ajuda é local e não envia mensagens.'}</span>
              </p>
            </div>
          </section>
        </div>
          </PresenceScene>
        )}
        </AnimatePresence>
      </main>

      <Dialog.Root open={dockOpen} onOpenChange={setDockOpen}>
        <Dialog.Portal container={previewRootRef.current ?? undefined}>
          <Dialog.Overlay className="reservei-preview__dock-overlay" />
          <Dialog.Content
            className="reservei-preview__dock-dialog"
            aria-describedby="preview-dock-description"
            onCloseAutoFocus={(event) => {
              const targetId = pendingFocusRef.current;
              if (!targetId) return;
              event.preventDefault();
              pendingFocusRef.current = null;
              document.getElementById(targetId)?.focus();
            }}
          >
            <motion.div
              className="reservei-preview__dock-dialog-header"
              initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.24, delay: 0.04 }}
            >
              <Dialog.Title>Resumo da prévia</Dialog.Title>
              <Dialog.Description id="preview-dock-description">Confira plano, ciclo, método, cupom e valores ilustrativos.</Dialog.Description>
            </motion.div>
            <motion.div
              className="reservei-preview__dock-dialog-body"
              initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.28, delay: 0.09 }}
            >
              <div className="reservei-preview__dock-dialog-summary">
                {stage === 'plans' ? (
                  <>
                    <strong>Plano selecionado: {PLAN_META[draftPlanId].name}</strong>
                    <span>O plano só será aplicado ao confirmar.</span>
                  </>
                ) : (
                  <>
                    <strong>{plan.name} · {cycle === 'monthly' ? 'Mensal' : 'Anual'}</strong>
                    <span>{formatPrice(firstCharge)} primeira cobrança</span>
                    <span>{cycle === 'annual' ? `Renovação integral: ${formatPrice(total)} por ano.` : `Renovação mensal: ${formatPrice(total)}.`}</span>
                    <span>Método: {method === 'pix' ? 'Pix demonstrativo' : 'Cartão demonstrativo'}</span>
                    <span>{couponApplied ? 'Cupom DEMO10 aplicado: 10% na primeira cobrança.' : 'Cupom: sem cupom aplicado.'}</span>
                    <ul>{plan.benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}</ul>
                  </>
                )}
              </div>
            </motion.div>
            <motion.div
              className="reservei-preview__dock-dialog-actions"
              initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.24, delay: 0.14 }}
            >
              <button type="button" className="reservei-preview__dock-cta" onClick={handleDockDialogPrimary} disabled={transitionActive} aria-disabled={transitionActive}>{dockCta}<ArrowRight size={18} aria-hidden="true" /></button>
              <Dialog.Close asChild><button type="button" className="reservei-preview__dock-dialog-close">Fechar resumo</button></Dialog.Close>
            </motion.div>
          </Dialog.Content>
        </Dialog.Portal>
      <div className={`reservei-preview__action-dock${dockOpen ? ' is-dialog-open' : ''}`} ref={dockRef}>
        <div className="reservei-preview__dock-total">
          <AnimatePresence initial={false} mode="popLayout">
            <motion.div
              className="reservei-preview__dock-total-value"
              key={stage === 'plans' ? `draft-${draftPlanId}` : `${cycle}-${firstCharge}-${couponApplied}`}
              initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: -4 }}
              transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
            >
              {stage === 'plans' ? (
                <>
                  <span>Plano {PLAN_META[draftPlanId].name}</span>
                  <small>confirme para aplicar</small>
                </>
              ) : (
                <>
                  <span>{cycle === 'annual' ? 'Anual' : 'Mensal'} · {formatPrice(firstCharge)}</span>
                  <small>{couponApplied ? 'primeira cobrança com DEMO10' : 'primeira cobrança ilustrativa'}</small>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
        <Dialog.Trigger asChild>
          <button type="button" className="reservei-preview__dock-summary">Ver resumo</button>
        </Dialog.Trigger>
        <button type="button" className="reservei-preview__dock-cta" onClick={handleDockPrimary} disabled={transitionActive} aria-disabled={transitionActive}>{dockCta}<ArrowRight size={18} aria-hidden="true" /></button>
      </div>
      </Dialog.Root>

      <footer className="reservei-preview__footer">
        <span>Prévia Reservei · sem cobrança</span>
        <span>Experiência demonstrativa local</span>
      </footer>
    </div>
  );
}

export const ReserveiCheckoutPreviewPage = ReserveiCheckoutPreview;
